import React from "react";
import {
  CheckCircle2,
  CircleAlert,
  Info,
  LoaderCircle,
  Sparkles,
  TriangleAlert,
  X,
} from "lucide-react";
import { toast, type ExternalToast } from "sonner";

export type ChiNotificationVariant =
  | "success"
  | "error"
  | "info"
  | "warning"
  | "loading";

export type ChiNotificationOptions = Omit<ExternalToast, "description"> & {
  description?: string;
  dedupeKey?: string;
};

export function createChiNotificationId(
  variant: ChiNotificationVariant,
  dedupeKey?: string,
) {
  if (!dedupeKey?.trim()) return undefined;

  const normalized = dedupeKey
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return `chi-${variant}-${normalized}`;
}

export const chiNotificationPresentation = {
  success: {
    accent: "#34D399",
    glow: "rgba(52,211,153,0.2)",
    eyebrow: "Connection confirmed",
    icon: CheckCircle2,
    live: "polite" as const,
  },
  error: {
    accent: "#FB7185",
    glow: "rgba(251,113,133,0.2)",
    eyebrow: "Action needed",
    icon: CircleAlert,
    live: "assertive" as const,
  },
  info: {
    accent: "#00D4FF",
    glow: "rgba(0,212,255,0.2)",
    eyebrow: "The Chi Alliance update",
    icon: Info,
    live: "polite" as const,
  },
  warning: {
    accent: "#F5A623",
    glow: "rgba(245,166,35,0.2)",
    eyebrow: "Please review",
    icon: TriangleAlert,
    live: "assertive" as const,
  },
  loading: {
    accent: "#A855F7",
    glow: "rgba(168,85,247,0.2)",
    eyebrow: "Working on it",
    icon: LoaderCircle,
    live: "polite" as const,
  },
};

export function ChiNotification({
  id,
  variant,
  title,
  description,
}: {
  id: string | number;
  variant: ChiNotificationVariant;
  title: string;
  description?: string;
}) {
  const config = chiNotificationPresentation[variant];
  const Icon = config.icon;

  return (
    <div
      role={variant === "error" || variant === "warning" ? "alert" : "status"}
      aria-live={config.live}
      style={{
        width: "min(420px, calc(100vw - 2rem))",
        position: "relative",
        display: "grid",
        gridTemplateColumns: "44px minmax(0, 1fr) 28px",
        gap: "0.85rem",
        alignItems: "start",
        overflow: "hidden",
        padding: "1rem",
        color: "#F8FAFC",
        background:
          "linear-gradient(135deg, rgba(15,22,41,0.98), rgba(8,12,24,0.98))",
        border: `1px solid ${config.accent}55`,
        borderRadius: "1rem",
        boxShadow: `0 20px 55px rgba(0,0,0,0.48), 0 0 34px ${config.glow}, inset 0 1px 0 rgba(255,255,255,0.07)`,
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: "0 auto 0 0",
          width: 3,
          background: `linear-gradient(180deg, ${config.accent}, #E91E8C)`,
          boxShadow: `0 0 18px ${config.accent}`,
        }}
      />

      <span
        aria-hidden="true"
        style={{
          width: 44,
          height: 44,
          display: "grid",
          placeItems: "center",
          borderRadius: "0.8rem",
          color: config.accent,
          background: `linear-gradient(145deg, ${config.accent}20, rgba(233,30,140,0.08))`,
          border: `1px solid ${config.accent}45`,
          boxShadow: `inset 0 0 18px ${config.glow}`,
        }}
      >
        <Icon
          size={21}
          strokeWidth={1.9}
          className={variant === "loading" ? "chi-notification-spinner" : undefined}
        />
      </span>

      <span style={{ minWidth: 0, paddingTop: 1 }}>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            marginBottom: "0.32rem",
            color: config.accent,
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
          }}
        >
          <Sparkles size={12} aria-hidden="true" />
          {config.eyebrow}
        </span>
        <strong
          style={{
            display: "block",
            color: "#FFFFFF",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "0.94rem",
            lineHeight: 1.35,
            fontWeight: 700,
          }}
        >
          {title}
        </strong>
        {description && (
          <span
            style={{
              display: "block",
              marginTop: "0.34rem",
              color: "#A8B4C8",
              fontFamily: "'Inter', sans-serif",
              fontSize: "var(--chi-body-compact)",
              lineHeight: 1.5,
            }}
          >
            {description}
          </span>
        )}
      </span>

      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => toast.dismiss(id)}
        style={{
          width: 28,
          height: 28,
          display: "grid",
          placeItems: "center",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "0.55rem",
          color: "#7F8BA3",
          background: "rgba(255,255,255,0.025)",
          cursor: "pointer",
          transition: "transform 160ms cubic-bezier(0.23,1,0.32,1), color 160ms ease, border-color 160ms ease",
        }}
        onMouseEnter={event => {
          event.currentTarget.style.color = "#FFFFFF";
          event.currentTarget.style.borderColor = `${config.accent}66`;
        }}
        onMouseLeave={event => {
          event.currentTarget.style.color = "#7F8BA3";
          event.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
        }}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

function showChiNotification(
  variant: ChiNotificationVariant,
  title: string,
  options: ChiNotificationOptions = {},
) {
  const {
    description,
    duration = 5200,
    dedupeKey,
    id: suppliedId,
    ...toastOptions
  } = options;
  const id = suppliedId ?? createChiNotificationId(variant, dedupeKey);

  return toast.custom(
    id => (
      <ChiNotification
        id={id}
        variant={variant}
        title={title}
        description={description}
      />
    ),
    {
      duration,
      id,
      ...toastOptions,
    },
  );
}

export const chiNotify = {
  success: (title: string, options?: ChiNotificationOptions) =>
    showChiNotification("success", title, options),
  error: (title: string, options?: ChiNotificationOptions) =>
    showChiNotification("error", title, options),
  info: (title: string, options?: ChiNotificationOptions) =>
    showChiNotification("info", title, options),
  warning: (title: string, options?: ChiNotificationOptions) =>
    showChiNotification("warning", title, options),
  loading: (title: string, options?: ChiNotificationOptions) =>
    showChiNotification("loading", title, {
      duration: Number.POSITIVE_INFINITY,
      ...options,
    }),
  promise: <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((value: T) => string);
      error: string | ((error: unknown) => string);
    },
  ) => {
    const id = showChiNotification("loading", messages.loading, {
      duration: Number.POSITIVE_INFINITY,
      dedupeKey: `promise-${messages.loading}`,
    });

    void promise.then(
      value => {
        const title =
          typeof messages.success === "function"
            ? messages.success(value)
            : messages.success;
        showChiNotification("success", title, { id });
      },
      error => {
        const title =
          typeof messages.error === "function"
            ? messages.error(error)
            : messages.error;
        showChiNotification("error", title, { id });
      },
    );

    return id;
  },
  dismiss: (id?: string | number) => toast.dismiss(id),
};

export function useChiNotify() {
  return chiNotify;
}
